import { creativeContentTypeOf, isVideoFile } from '@/entities/media-campaign';

const MB = 1024 * 1024;

/** The two files the server parses, as extensions, owned here so nothing spells them again. */
export const PLAN_EXTENSIONS = ['.xlsx', '.csv'];
export const BUNDLE_EXTENSIONS = ['.zip'];

/**
 * Mirrors media-api's own ceilings: `MAX_SHEET_BYTES` in
 * `media-delivery/parsing/media-plan-bundle.ts`, `MAX_BUNDLE_BYTES` in
 * `media-delivery/delivery-jobs.controller.ts`, and `maxBytesFor` in
 * `media-delivery/assets/asset-key.ts`, whose image limit is Meta's rather than ours and whose
 * video limit is what a publish can send.
 *
 * Not `MAX_BUNDLE_INFLATED_BYTES`, which sits next to `MAX_SHEET_BYTES` and is the 250 MB an
 * archive may unpack to, not the 100 MB it may arrive as.
 *
 * Raising one side alone is what this comment exists to prevent. The creative ceilings are
 * applied at `/assets/confirm`, which runs after the object has already landed in storage,
 * so a dialog that allows more than the server does spends the whole upload before the
 * refusal arrives.
 */
const MAX_PLAN_BYTES = 10 * MB;
const MAX_BUNDLE_BYTES = 100 * MB;
const MAX_CREATIVE_IMAGE_BYTES = 30 * MB;
const MAX_CREATIVE_VIDEO_BYTES = 100 * MB;

const matches = (file: File, extensions: string[]): boolean =>
  extensions.some((extension) => file.name.toLowerCase().endsWith(extension));

/**
 * A file the operating system put in the folder, which nobody dragging it chose.
 *
 * react-dropzone already drops `.DS_Store` and `Thumbs.db`. What it does not drop is the
 * lock file Excel writes beside an open workbook: `~$q4-plan.xlsx` ends in `.xlsx`, so a
 * folder dragged in while the plan is still open reads as two plans, and the refusal names
 * a file the operator cannot see on disk. `._` covers the resource forks macOS leaves on
 * non-native volumes, which arrive the same way.
 */
export const isSidecar = (file: File): boolean =>
  file.name.startsWith('~$') || file.name.startsWith('._');

export const isBundle = (file: File): boolean => matches(file, BUNDLE_EXTENSIONS);

export const isPlan = (file: File): boolean => matches(file, PLAN_EXTENSIONS);

/**
 * The ceiling one file is held to, or undefined for a file this dialog does not take at all.
 *
 * One call answers both questions on purpose: asking "is this accepted?" and "how large may
 * it be?" separately means reading the extension twice, which is how two lists drift apart.
 */
export function limitFor(file: File): number | undefined {
  if (isBundle(file)) return MAX_BUNDLE_BYTES;
  if (isPlan(file)) return MAX_PLAN_BYTES;
  if (!creativeContentTypeOf(file)) return undefined;
  return isVideoFile(file) ? MAX_CREATIVE_VIDEO_BYTES : MAX_CREATIVE_IMAGE_BYTES;
}
